/**
 * Param Typings for link-generators
 * - each param here builds on eachother, mix and match
 *   to find the proper shape for your link
 *
 *   ex. LinkGenerator<AccountParams & ProjectParams>
 *     - would require both an accountId and a projectId, but not require a resourceId
 */

import { ModelLayer } from '@dbt-labs/dbt-dag';

import {
  AccessLevel,
  AppliedModelSort,
  AutoExposureBiProvider,
  RuleCategoryEnum,
  RuleNameEnum,
  RuleSeverityEnum,
} from '@/__generated__/graphql';
import { NavigationResourceType } from '@/panel/ResourcesFilter/selectedResource';
import { UrlEnvironmentType } from '@/typings/url';

export const granularityTypes = ['account', 'project', 'column'] as const;
export type GranularityType = (typeof granularityTypes)[number];

export const viewTypes = ['lineage', 'list'] as const;
export type ViewType = (typeof viewTypes)[number];

export interface LinkGeneratorParams {
  /** All featureFlags are passed into the generators */
  // featureFlags?: FeatureFlagSet;
}

/**
 * Linkables that require an accountId
 */
export interface AccountParams extends LinkGeneratorParams {
  accountId: number;
}

/**
 * Linkables that require an account domain
 */
export interface AccountDomainParams extends LinkGeneratorParams {
  domain: string;
}

/**
 * Linkables that require a ResourceType
 */
export interface ResourceTypeParams extends LinkGeneratorParams {
  resourceType: NavigationResourceType;
}

/**
 * Linkables that require a ResourceType
 */
export type ResourceFilterTypeParams = LinkGeneratorParams &
  (
    | {
        resourceType: NavigationResourceType;
      }
    | {
        resourceType: 'model';
        access?: 'protected' | 'public' | 'private';
        modelingLayer?: ModelLayer;
        lastRunStatus?: 'error' | 'skipped' | 'success';
        sort?: AppliedModelSort;
      }
  );

/**
 * Linkables that require a dbt Unique ID
 */
export interface UniqueIdParams extends LinkGeneratorParams {
  dbtUniqueId: string;
}
export interface AssetIdParams extends LinkGeneratorParams {
  assetId: string;
}

/**
 * Linkables that require a environmentId
 */
export interface EnvironmentIdParams extends LinkGeneratorParams {
  environmentId: number;
}

/**
 * Linkables that require a sourceName
 */
export interface SourceNameParams extends LinkGeneratorParams {
  sourceName: string;
}

/**
 * Linkables that require a packageName
 */
export interface PackageNameParams extends LinkGeneratorParams {
  packageName: string;
}

/**
 * Linkables that require a databaseName
 */
export interface DatabaseNameParams extends LinkGeneratorParams {
  databaseName: string;
}

/**
 * Linkables that require a databaseName and a schemaName
 */
export interface SchemaNameParams extends LinkGeneratorParams {
  schemaName: string;
}

/**
 * Linkables that require a selector
 */
export interface SelectorParams extends LinkGeneratorParams {
  selector: string;
}

export interface SearchParams extends LinkGeneratorParams {
  search?: string;
  filters?: string;
}

export interface JobParams extends LinkGeneratorParams {
  jobId: number;
}

export interface RunParams extends LinkGeneratorParams {
  runId: number;
}

export interface ProjectParams extends LinkGeneratorParams {
  projectId: number;
}

export interface ConnectionParams extends LinkGeneratorParams {
  connectionId: number;
}

export interface GrainParams extends LinkGeneratorParams {
  grain: GranularityType;
}

export interface ViewParams extends LinkGeneratorParams {
  view: ViewType;
}

export interface TabParams<TTab extends string = string> extends LinkGeneratorParams {
  tab: TTab;
}

export interface ColumnNameParams extends LinkGeneratorParams {
  columnName: string;
}

export interface LineageIntegrationParams extends LinkGeneratorParams {
  lineageIntegrationId?: number;
}

export interface ExposureTileParams extends LinkGeneratorParams {
  metadataUri: string;
  uniqueId: string;
  environmentType: UrlEnvironmentType;
  environmentId: number;
  token: string;
  biResourceId?: string;
  autoBiProvider?: AutoExposureBiProvider | null;
}

/**
 * Which column did you come from
 */
export interface OriginColumnParams extends LinkGeneratorParams {
  originColumn: string;
}
export interface OriginProjectParams extends LinkGeneratorParams {
  originProject: number;
}

export interface SelectColumnParams extends LinkGeneratorParams {
  selectedColumn: string;
}

export type IdeDeeplinkParams = {
  file?: string[];
};

/**
 * Linkables that are scoped to a project
 */
export type UrlProjectParams = {
  accountId: number;
  projectId: number;
  environmentType: UrlEnvironmentType;
};

export type ResourceFilterParams = {
  modelingLayer?: ModelLayer;
  access?: AccessLevel;
};

export interface RecommendationFilters extends LinkGeneratorParams {
  category?: RuleCategoryEnum;
  ruleName?: RuleNameEnum;
  severity?: RuleSeverityEnum;
}

export type GetParamType<T> = T extends (params: infer P) => string ? P : never;

export interface LinkGenerator<P extends LinkGeneratorParams> {
  (params: P): string;
}
